import { Navigate } from "react-router";

import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext.ts";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types.ts";
import { AppRoutes } from "@/router/AppRoutes.ts";

/**
 * Usage: wrap your component with this and if user does not have permission, it will navigate to the main page
 * @param WrappedComponent
 * @param permission Change this with your own logic for permissions
 * @returns
 */
function withAuthorization<T extends object>(
  WrappedComponent: React.ComponentType<T>,
  permission: UserPermission | UserPermission[]
) {
  const ComponentWithAuth = (props: T) => {
    const { hasPermission } = useCurrentUserContext();
    const permissions = Array.isArray(permission) ? permission : [permission];
    const isAuthorised = permissions.some((p) => hasPermission(p));

    if (!isAuthorised) {
      return <Navigate to={AppRoutes.Default} replace />;
    }

    return <WrappedComponent {...props} />;
  };

  const wrappedName = (WrappedComponent.displayName ?? WrappedComponent.name) || "Component";
  ComponentWithAuth.displayName = `WithAuth(${wrappedName})`;

  return ComponentWithAuth;
}

export default withAuthorization;
